import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import TeamAnalytics from '../components/TeamAnalytics';
import TeamRankings from '../components/TeamRankings';
import { calculateTeamAnalytics, calculateTeamRankings } from '../utils/teamAnalytics';

const TeamDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [gameResults, setGameResults] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadTeamData();
  }, [user, navigate]);

  const loadTeamData = async () => {
    try {
      setLoading(true);

      // Get user document to find team ID
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        console.error('User document not found');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const teamId = userData.teamId;

      if (!teamId) {
        // User doesn't have a team yet
        setLoading(false);
        return;
      }

      // Get team document
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (!teamDoc.exists()) {
        console.error('Team document not found');
        setLoading(false);
        return;
      }

      const teamData = { id: teamDoc.id, ...teamDoc.data() };
      setTeam(teamData);

      // Load game results for analytics
      await loadGameResults(teamId);

    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameResults = async (teamId) => {
    try {
      // Query game results for this team
      const gameResultsQuery = query(
        collection(db, 'gameResults'),
        where('teamId', '==', teamId)
      );
      const gameResultsSnapshot = await getDocs(gameResultsQuery);
      
      const results = gameResultsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setGameResults(results);

      // Calculate analytics
      const teamAnalytics = calculateTeamAnalytics(results);
      setAnalytics(teamAnalytics);

      // Load all teams for rankings calculation
      await loadRankings(teamAnalytics, team);

    } catch (error) {
      console.error('Error loading game results:', error);
    }
  };

  const loadRankings = async (teamAnalytics, teamData) => {
    try {
      // Get all teams for rankings calculation
      const teamsQuery = query(collection(db, 'teams'));
      const teamsSnapshot = await getDocs(teamsQuery);
      
      const allTeams = teamsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate rankings
      const teamRankings = calculateTeamRankings(allTeams, teamAnalytics, teamData?.location);
      setRankings(teamRankings);

    } catch (error) {
      console.error('Error loading rankings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading team dashboard...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <Logo />
                <h1 className="ml-4 text-xl font-semibold text-white">Team Dashboard</h1>
              </div>
            </div>
          </div>
        </div>

        {/* No Team State */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">No Team Found</h2>
            <p className="text-gray-300 mb-6">
              You don't have a team yet. Create a team to start competing in league games.
            </p>
            <button
              onClick={() => navigate('/team/create')}
              className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-2 rounded-lg"
            >
              Create Team
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Logo />
              <h1 className="ml-4 text-xl font-semibold text-white">{team.name}</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/team/create')}
                className="text-gray-400 hover:text-white"
              >
                Edit Team
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Team Info */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{team.name}</h2>
              <p className="text-gray-300">{team.description}</p>
              <div className="mt-2 text-sm text-gray-400">
                <span>{team.location?.city}, {team.location?.state}</span>
                <span className="mx-2">•</span>
                <span>League ID: {team.leagueId}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-400">Contact</div>
              <div className="text-white font-medium">{team.contactPerson?.name}</div>
              <div className="text-sm text-gray-300">{team.contactPerson?.email}</div>
            </div>
          </div>
        </div>

        {/* Analytics and Rankings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <TeamAnalytics analytics={analytics} loading={loading} />
          <TeamRankings rankings={rankings} loading={loading} />
        </div>

        {/* Recent Games */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Recent Games</h3>
          {gameResults.length === 0 ? (
            <p className="text-gray-400">No games played yet</p>
          ) : (
            <div className="space-y-4">
              {gameResults.slice(0, 5).map((game) => (
                <div key={game.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{game.gameTitle || 'Game'}</div>
                      <div className="text-sm text-gray-300">
                        {game.gameDate ? new Date(game.gameDate).toLocaleDateString() : 'Unknown date'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">Score: {game.score || 0}</div>
                      <div className={`text-sm ${game.won ? 'text-green-400' : 'text-red-400'}`}>
                        {game.won ? 'Won' : 'Lost'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDashboard;